/**
 * LessonSummaryCard.jsx — Cycle 6 · inline companion summary at the top
 * of every lesson.
 *
 * Fetches 3-4 short sentences from POST /api/teacher (mode=summary),
 * cached per (code, age_tier). Never blocks the lesson — hides if the
 * call fails.
 */
import React, { useEffect, useState } from "react";
import { Sparkles, Volume2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Reuse the same age-band mapping used elsewhere.
const TIER_TO_AGE_BAND = {
  seedling: "5-7", seedlings: "5-7",
  explorer: "8-10", explorers: "8-10",
  pioneer: "11-15", pioneers: "11-15",
};

export default function LessonSummaryCard({ code, outcome, subject, ageTier }) {
  const [state, setState] = useState({ loading: true, text: "", error: false });
  useEffect(() => {
    if (!code) return undefined;
    let dead = false;
    const band = TIER_TO_AGE_BAND[ageTier] || ageTier || "8-10";
    fetch(`${API}/api/teacher`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        code, outcome: outcome || "", subject: subject || "general",
        age_tier: band, mode: "summary",
      }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => { if (!dead) setState({ loading: false, text: j.text || "", error: false }); })
      .catch(() => { if (!dead) setState({ loading: false, text: "", error: true }); });
    return () => { dead = true; };
  }, [code, outcome, subject, ageTier]);

  const speak = () => {
    if (!state.text || typeof window === "undefined" || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const u = new SpeechSynthesisUtterance(state.text);
      u.rate = 0.95; u.lang = "en";
      window.speechSynthesis.speak(u);
    } catch { /* silent */ }
  };

  if (state.error) return null;
  return (
    <section
      data-testid="lesson-summary-card"
      className="rounded-2xl border-[3px] border-slate-900 bg-gradient-to-br from-yellow-100 via-white to-emerald-50 p-4 md:p-5 shadow-[6px_6px_0_0_rgba(15,23,42,1)]"
      aria-live="polite"
    >
      <div className="flex items-center justify-between">
        <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700">
          <Sparkles size={12} strokeWidth={3} /> Companion summary
        </p>
        {state.text && (
          <button
            type="button" onClick={speak}
            data-testid="lesson-summary-speak"
            aria-label="Read summary aloud"
            className="inline-flex items-center justify-center h-12 w-12 rounded-full border-2 border-slate-900 bg-white shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform"
          >
            <Volume2 size={18} strokeWidth={3} />
          </button>
        )}
      </div>
      {state.loading ? (
        <p className="mt-2 text-sm font-medium text-slate-500 animate-pulse">
          Preparing a short summary…
        </p>
      ) : (
        <p className="mt-2 text-[15px] md:text-base leading-relaxed text-slate-800 font-medium" data-testid="lesson-summary-text">
          {state.text}
        </p>
      )}
    </section>
  );
}
